import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgConfigProvider } from './contexts/OrgConfigContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import ItemDetail from './pages/ItemDetail';
import Users from './pages/Users';
import ItemTypes from './pages/ItemTypes';
import Activity from './pages/Activity';
import Inspections from './pages/Inspections';
import Import from './pages/Import';
import DepartmentConfig from './pages/DepartmentConfig';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Account from './pages/Account';
import MemberGear from './pages/MemberGear';
import ForceChangePassword from './pages/ForceChangePassword';

type UserRole = 'admin' | 'quartermaster' | 'user';

function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: UserRole[] }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" />;
  return <>{children}</>;
}

// Standard users go straight to inventory; admins/quartermasters get the dashboard
function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === 'user') return <Navigate to="/inventory" replace />;
  return <Layout><Dashboard /></Layout>;
}

function AppRoutes() {
  const { user, loading, mustChangePassword } = useAuth();

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  // If user is logged in but must change password, show only that screen
  if (user && mustChangePassword) {
    return <ForceChangePassword />;
  }

  // Determine the post-login redirect target
  const loginRedirect = user?.role === 'user' ? '/inventory' : '/';

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={loginRedirect} /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to={loginRedirect} /> : <Signup />} />
      <Route path="/forgot-password" element={user ? <Navigate to={loginRedirect} /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to={loginRedirect} /> : <ResetPassword />} />
      <Route path="/accept-invite" element={user ? <Navigate to={loginRedirect} /> : <AcceptInvite />} />
      <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><Layout><Inventory /></Layout></ProtectedRoute>} />
      <Route path="/inventory/:id" element={<ProtectedRoute><Layout><ItemDetail /></Layout></ProtectedRoute>} />
      <Route path="/retired" element={<ProtectedRoute><Layout><Inventory retired /></Layout></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']}><Layout><Users /></Layout></ProtectedRoute>} />
      <Route path="/item-types" element={<ProtectedRoute allowedRoles={['admin', 'quartermaster']}><Layout><ItemTypes /></Layout></ProtectedRoute>} />
      <Route path="/department" element={<ProtectedRoute allowedRoles={['admin', 'quartermaster']}><Layout><DepartmentConfig /></Layout></ProtectedRoute>} />
      <Route path="/inspections" element={<ProtectedRoute allowedRoles={['admin', 'quartermaster']}><Layout><Inspections /></Layout></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute allowedRoles={['admin', 'quartermaster']}><Layout><Import /></Layout></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><Layout><Activity /></Layout></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Layout><Account /></Layout></ProtectedRoute>} />
      <Route path="/members/:id/gear" element={<ProtectedRoute><Layout><MemberGear /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrgConfigProvider>
          <AppRoutes />
        </OrgConfigProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
